import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import ReactSelect from 'react-select';

import { ValidationError } from '../../components';

import * as components from './components';
import css from './FieldSelectCustom.module.css';

const FieldSelectCustomComponent = ({
  rootClassName,
  className,
  selectClassName,
  id,
  label,
  input,
  meta,
  children,
  onChange,
  options,
  disabled,
  ...rest
}) => {
  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched, error } = meta;

  const inputValue = input.value;
  const isMulti = !!input.multiple;
  const hasError = touched && invalid && error;
  const hasValue = !!inputValue?.length;

  const selectedOptions = (() => {
    if (isMulti) {
      return inputValue?.map?.(selected => options.find(({ value }) => value === selected));
    } else {
      return options.find(({ value }) => value === inputValue);
    }
  })();

  const selectClasses = classNames(css.select, {
    [selectClassName]: selectClassName,
    [css.selectError]: hasError,
    [css.hasValue]: hasValue,
  });

  const handleChange = event => {
    if (event.constructor.name === 'SyntheticEvent') {
      input.onChange(event);
      onChange?.(event);
    } else {
      const newValue = Array.isArray(event) ? event.map(({ value }) => value) : event?.value;
      input.onChange(newValue);
      onChange?.(newValue);
    }
  };

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      <ReactSelect
        className={selectClasses}
        classNamePrefix="react-select"
        id={id && `${id}-desktop`}
        components={components}
        options={options}
        isMulti={isMulti}
        placeholder={false}
        isClearable={false}
        hideSelectedOptions={false}
        isSearchable={false}
        closeMenuOnSelect={false}
        isDisabled={disabled}
        {...input}
        value={selectedOptions}
        onChange={handleChange}
        {...rest}
      />
      <select
        className={classNames(selectClasses, css.mobileSelect)}
        id={id && `${id}-mobile`}
        {...input}
        onChange={handleChange}
        {...rest}
      >
        {options.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      {label ? (
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
      ) : null}
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

FieldSelectCustomComponent.defaultProps = {
  rootClassName: null,
  className: null,
  selectClassName: null,
  id: null,
  label: null,
  children: null,
};

const { string, object, node } = PropTypes;

FieldSelectCustomComponent.propTypes = {
  rootClassName: string,
  className: string,
  selectClassName: string,
  id: string,
  label: string,
  input: object.isRequired,
  meta: object.isRequired,
  children: node,
};

const FieldSelectCustom = props => {
  const { multiple, defaultValue } = props;

  return <Field {...props} defaultValue={multiple ? defaultValue : null} />;
};

FieldSelectCustom.defaultProps = {
  component: FieldSelectCustomComponent,
  type: 'select',
  defaultValue: [],
};

export default FieldSelectCustom;
